import { createAPIPath } from '@/config'
import { container, TYPES } from '@/libs/di/inversify.config'
import type { APIConfig } from '@/apis/infrastructures'
import router, { createLoginRedirectHref, isRequireAuthRoute } from '@/router'
// MEMO:
// react に依存しないように @apollo/client ではなく @apollo/client/core を使用する
// https://www.apollographql.com/docs/react/migrating/apollo-client-3-migration#using-apollo-client-without-react
import type { ApolloLink } from '@apollo/client/core'
import {
  ApolloClient,
  createHttpLink,
  from,
  InMemoryCache,
} from '@apollo/client/core'
import type { NetworkError } from '@apollo/client/errors'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import type { ServerError } from '@apollo/client/link/utils'
import { DefaultApolloClient } from '@vue/apollo-composable'
import type { Plugin } from 'vue'

import { captureMessage } from '@/newrelic'
import { relayStylePagination } from '@apollo/client/utilities'
import type { GraphQLFormattedError } from 'graphql'
import generateIntrospection from '../apollo.fragment.generated'

export const defaultClient = new ApolloClient({
  link: buildLink(),
  devtools: {
    enabled: import.meta.env.MODE !== 'production',
  },
  cache: new InMemoryCache({
    possibleTypes: generateIntrospection.possibleTypes,
    typePolicies: {
      Query: {
        fields: {
          // 絞り込み状態によってキャッシュを切り替えるため、追加のキャッシュキーを第一引数で指定する
          // 事象: https://400f-jp.slack.com/archives/C037A321U8K/p1655094591078789?thread_ts=1654851578.229439&cid=C037A321U8K
          // ドキュメント: https://www.apollographql.com/docs/react/pagination/key-args/#setting-keyargs
          chatRooms: relayStylePagination(['statuses']),
          colleagueChatRooms: relayStylePagination(),
          karten: relayStylePagination(),
        },
      },
      Viewer: {
        // viewer は 1クライアントにつき、対応する 1つのオブジェクトのみを扱う
        // よって、key による識別が不要なので keyFields を空にする
        // 参考： https://www.apollographql.com/docs/react/caching/cache-configuration/#customizing-cache-ids
        //
        // また、ログアウト時に apollo の store をクリアしているので、ユーザーの切り替えによって発生する key の識別は不要になっている
        // .clearStore() などで全文検索すると該当箇所が見つかるはず
        keyFields: [],
        fields: {
          // Staffオブジェクトのキャッシュを無効化
          // 各Queryでid相当のデータがなく、apollo client側で正規化がうまくできないため、とりあえずキャッシュを利用しない形式に設定
          // apollo clientが表示している警告
          // https://www.apollographql.com/docs/react/errors#%7B%22version%22%3A%223.14.0%22%2C%22message%22%3A15%2C%22args%22%3A%5B%22staff%22%2C%22Viewer%22%2C%22either%20ensure%20all%20objects%20of%20type%20Staff%20have%20an%20ID%20or%20a%20custom%20merge%20function%2C%20or%20%22%2C%22Viewer.staff%22%2C%22%7B%5Cn%20%20%5C%22__typename%5C%22%3A%20%5C%22Staff%5C%22%2C%5Cn%20%20%5C%22role%5C%22%3A%20%5C%22ENTRANCE_MANAGER%5C%22%5Cn%7D%22%2C%22%7B%5Cn%20%20%5C%22__typename%5C%22%3A%20%5C%22Staff%5C%22%2C%5Cn%20%20%5C%22email%5C%22%3A%20%5C%22okaneko_investment%40400f.jp%5C%22%5Cn%7D%22%5D%7D
          staff: {
            merge: false, // 常に新しいデータで置き換え（マージしない）
          },
        },
      },
      PlannerProfile: {
        // PlannerProfile は 1クライアントにつき、ログインしている Planner のもののみを扱う
        keyFields: [],
      },
      AutoPostKartePlannerCommentSetting: {
        // AutoPostKartePlannerCommentSetting は 1クライアントにつき、ログインしている Planner のもののみを扱う
        keyFields: [],
      },
      Contract: {
        // Contract は 1クライアントにつき、ログインしている Planner のもののみを扱う
        keyFields: [],
      },
      ChatRoom: {
        fields: {
          // Userフィールドのキャッシュを無効化
          // 各Queryでid相当のデータがなく、apollo client側で正規化がうまくできないため、とりあえずキャッシュを利用しない形式に設定
          // apollo clientが表示している警告
          // https://www.apollographql.com/docs/react/errors#%7B%22version%22%3A%223.14.0%22%2C%22message%22%3A15%2C%22args%22%3A%5B%22user%22%2C%22ChatRoom%22%2C%22either%20ensure%20all%20objects%20of%20type%20User%20have%20an%20ID%20or%20a%20custom%20merge%20function%2C%20or%20%22%2C%22ChatRoom.user%22%2C%22%7B%5Cn%20%20%5C%22__typename%5C%22%3A%20%5C%22User%5C%22%2C%5Cn%20%20%5C%22lineActivationUser%5C%22%3A%20null%5Cn%7D%22%2C%22%7B%5Cn%20%20%5C%22__ref%5C%22%3A%20%5C%22User%3A1181223%5C%22%5Cn%7D%22%5D%7D
          user: {
            merge: false, // 常に新しいデータで置き換え（マージしない）
          },
        },
      },
      UIControl: {
        // UIControl は ID を持たないシングルトンオブジェクトなので keyFields を空にする
        // これによりキャッシュキーが不要となり、マージエラーを回避できる
        // https://www.apollographql.com/docs/react/errors#%7B%22version%22%3A%223.12.9%22%2C%22message%22%3A15%2C%22args%22%3A%5B%22uiControl%22%2C%22Query%22%2C%22either%20ensure%20all%20objects%20of%20type%20UIControl%20have%20an%20ID%20or%20a%20custom%20merge%20function%2C%20or%20%22%2C%22Query.uiControl%22%2C%22%7B%5Cn%20%20%5C%22__typename%5C%22%3A%20%5C%22UIControl%5C%22%2C%5Cn%20%20%5C%22editCorporation%5C%22%3A%20false%2C%5Cn%20%20%5C%22editStaffs%5C%22%3A%20true%2C%5Cn%20%20%5C%22useLineMessageFeatures%5C%22%3A%20true%5Cn%7D%22%2C%22%7B%5Cn%20%20%5C%22__typename%5C%22%3A%20%5C%22UIControl%5C%22%2C%5Cn%20%20%5C%22viewBranchReport%5C%22%3A%20false%2C%5Cn%20%20%5C%22viewCorporationReport%5C%22%3A%20false%2C%5Cn%20%20%5C%22useLineMessageFeatures%5C%22%3A%20true%2C%5Cn%20%20%5C%22viewMatchedRooms%5C%22%3A%20true%2C%5Cn%20%20%5C%22viewBranchMemberRooms%5C%22%3A%20false%2C%5Cn%20%20%5C%22viewCorporationMemberRooms%5C%22%3A%20false%5Cn%7D%22%5D%7D
        keyFields: [],
      },
    },
  }),
})

const plugin = ((): Plugin => {
  // eslint-disable-next-line fp/no-let
  let called = false

  return app => {
    if (called) {
      console.warn('ApolloPlugin has been already installed')
      return
    }
    called = true

    app.provide(DefaultApolloClient, defaultClient)
  }
})()

export default plugin

const isServerError = (error?: NetworkError): error is ServerError => {
  if (!error) {
    return false
  }

  return 'statusCode' in error
}

function buildLink(): ApolloLink {
  const httpLink = createHttpLink({
    uri: createAPIPath('federationGateway', '/gateway/planner/query'),
    credentials: 'include',
  })

  const authLink = setContext((_, { headers }) => {
    const apiConfig = container.get<APIConfig>(TYPES.APIConfig)
    const { apiToken } = apiConfig

    const routeParams = router.currentRoute.value.params as Record<
      string,
      string
    >

    const entranceGUID = routeParams.entranceGUID as string | undefined
    return {
      headers: {
        ...headers,
        ...(apiToken && { 'X-API-TOKEN': apiToken.toString() }),
        ...(entranceGUID && {
          '400f-biz-entrance-guid': entranceGUID,
        }),
      },
    }
  })

  const errorLink = onError(
    ({ networkError, graphQLErrors, operation, response }) => {
      captureMessage(JSON.stringify(graphQLErrors), {
        gqlOperation: operation.operationName,
        gqlVariables: operation.variables,
        gqlResponse: JSON.stringify(response),
      })

      if (isServerError(networkError) && networkError.statusCode === 422) {
        window.alert('エラーが発生しました。リロードしてください。')
        return
      }

      const currentRoute = router.currentRoute.value
      if (
        hasAuthenticatedError(networkError, graphQLErrors) &&
        isRequireAuthRoute(currentRoute)
      ) {
        location.href = createLoginRedirectHref(
          currentRoute.path,
          currentRoute.query
        )
      }
    }
  )
  return from([errorLink, authLink, httpLink])
}

export const hasAuthenticatedError = (
  networkError: NetworkError | undefined,
  graphQLErrors: readonly GraphQLFormattedError[] | undefined
): boolean => {
  if (isServerError(networkError) && networkError.statusCode === 401) {
    return true
  }

  if (!graphQLErrors) {
    return false
  }

  const codes = graphQLErrors.flatMap(e => {
    return e.extensions ? e.extensions.code : []
  })

  return codes.includes('UNAUTHENTICATED')
}
